<template>
	<div
		v-if="shouldShowGasStation"
		class="space-y-4"
	>
		<slot name="header" />
		<div v-if="pending">
			<div v-for="index in 5" :key="index" class="skeleton-gradient h-5 w-full mb-2 rounded-lg" />
			<div class="skeleton-gradient h-8 w-36 mt-3 rounded-xl" />
		</div>

		<template v-else>
			<StoryblokComponent
				v-for="info in hypermarketDetails!.gasStation!.stateInfo ?? []"
				:key="info._uid"
				:blok="info"
			/>
			<p v-if="hasOnlySelfServiceFuelPumpOpen" class="font-bold">
				Samoobslužné tankování je v&nbsp;provozu.
			</p>
			<FuelPricelistTable
				v-if="isAnyFuelPumpOpen && fuelPricelist"
				:pricelist="fuelPricelist"
				class="w-full"
			/>

			<AbsoluteInternalLink
				:to="branchSpecificPath({name: 'sluzby-slug', hash: '#informace', params: {slug: 'cerpaci-stanice-a-myci-linka'}})"
				class="btn btn-secondary btn-xs justify-center text-center max-xs:grow max-xl:hidden shadow-sm"
				:aria-label="`Zobrazit více informací o mycí lince v pobočce ${selectedHypermarket}`"
				@click="handleClick('Více informací | Čerpací stanice')"
			>
				<NuxtIcon
					name="custom/arrow-right"
					class="text-lg transition duration-300"
					filled
					aria-hidden="true"
				/>
				Více informací
			</AbsoluteInternalLink>
		</template>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '../../../stores/storyblokHypermarkets';

	type Emits = {
		(e: 'update:click', text: string): void
		(e: 'condition-met', payload: boolean): void
	};

	const emit = defineEmits<Emits>();

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket} = storeToRefs(hypermarketsStore);
	const branchSpecificPath = useBranchSpecificPath();

	const {data: hypermarketDetails, loading: pending} = useHypermarketDetails(selectedHypermarket);

	const fuelPricelist = computed(() => firstBlok(hypermarketDetails.value?.gasStation?.fuelPricelist));

	const isAnyFuelPumpOpen = computed(() => (
		!!hypermarketDetails.value?.gasStation?.fuelPump?.[0]?.open
		|| !!hypermarketDetails.value?.gasStation?.selfServiceFuelPump?.[0]?.open
	));

	const hasOnlySelfServiceFuelPumpOpen = computed(() => (
		!hypermarketDetails.value?.gasStation?.fuelPump?.[0]?.open
		&& hypermarketDetails.value?.gasStation?.selfServiceFuelPump?.[0]?.open
	));

	const shouldShowGasStation = computed(() => (
		!!(hypermarketDetails.value && hypermarketDetails.value.gasStation)
		&& (isAnyFuelPumpOpen.value || !!hypermarketDetails.value.gasStation.stateInfo?.length)
	));

	watch(shouldShowGasStation, (isMet) => {
		emit('condition-met', isMet);
	}, {immediate: true});

	function handleClick(text: string) {
		emit('update:click', text);
	}
</script>
